import { Badge } from '../../../atoms/Badge/Badge';
import type { TableColumn, TableRowData } from '../../../organisms/Table/Table';
import { NONE } from '../shared/cardShell';

/**
 * The columns that draw the `Top hashtags used` ranking, and its rows.
 *
 * Shared because the same ranking is drawn twice: the widget on Trends
 * (951:26706) shows the top eight, and the `View all` modal (2211:92097) shows
 * every one of them. Only the widths differ between the two, which is why they
 * are a parameter and everything else is not.
 */

export type HashtagSentiment = 'positive' | 'neutral' | 'negative';

/** One ranked tag, as drawn. */
export type HashtagEntry = {
  /** `#mygp`, as written. Also the row key. */
  label: string;
  /** The tone most of its mentions carry, or `null` when there are none. */
  sentiment: HashtagSentiment | null;
  /** `No. of mentions`, formatted. */
  mentions: string;
  /** `% of overall` — a share of every **tagged** mention in the period, formatted. */
  share: string;
};

export function hashtagTableRows(entries: readonly HashtagEntry[]): TableRowData[] {
  return entries.map((entry) => ({
    key: entry.label,
    cells: {
      hashtag: { text: entry.label },
      sentiment: {
        content: entry.sentiment ? (
          /*
           * The same three tones the cloud paints, in the `Badge` the frame
           * draws — `Badge`'s surfaces alias the `component/progress` set, so
           * the table's pills and the clouds' words cannot drift apart.
           */
          <Badge type={entry.sentiment}>
            {`${entry.sentiment[0]!.toUpperCase()}${entry.sentiment.slice(1)}`}
          </Badge>
        ) : (
          NONE
        ),
      },
      mentions: { text: entry.mentions },
      /* A real percentage, where the frame repeats the mention count. */
      share: { text: entry.share },
    },
  }));
}

/**
 * The four columns, at whichever width the surface draws them.
 *
 * **No `Rank`.** The widget carried one until 2026-09-23, when the frame
 * dropped it along with the pagination: with eight rows and no page to be on,
 * a rank column counts down the screen and says nothing the order does not.
 */
export function hashtagColumns(widths: readonly [string, string, string, string]): TableColumn[] {
  return [
    { key: 'hashtag', header: 'Hashtag', width: widths[0] },
    { key: 'sentiment', header: 'Sentiment', cellType: 'badge', width: widths[1] },
    { key: 'mentions', header: 'No. of mentions', width: widths[2] },
    {
      key: 'share',
      header: '% of overall',
      helpText: 'Share of every tagged mention in the period, not of every mention.',
      width: widths[3],
    },
  ];
}
